import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import {
  fillSmsTemplate,
  refuseSmsTemplate,
  segmentsFor,
  SMS_KINDS,
  SMS_PLACEHOLDERS,
  type SmsKind,
} from '@jt/domain';
import { api } from '../lib/api';
import type { SettingsPayload } from '../lib/types';

/**
 * The wording of every SMS, with what it will look like and what it will cost.
 *
 * ## Why the preview is not optional
 *
 * A template is written once and sent thousands of times, and the writer never
 * sees the result — the landlord does. Everything that goes wrong here goes
 * wrong invisibly: a placeholder that does not exist, a line that collapses
 * when its value is missing, a curly apostrophe that doubles the bill. So the
 * preview is filled with realistic values and sits beside the box, not behind
 * a button.
 *
 * ## Why the cost is on screen
 *
 * One character over 160 is two texts, for every recipient, for ever. That is
 * not a thing anybody can be expected to count, and it is the single most
 * expensive mistake available on this screen.
 */

/**
 * Values the preview is filled with.
 *
 * Deliberately realistic rather than `{like this}`: the point is to show the
 * message somebody will actually receive, and a preview full of braces hides
 * exactly the problems it exists to reveal — a name that makes a line too
 * long, a token that wraps badly.
 *
 * `{units}` is null on purpose. Kenya Power states units *or* an amount, so
 * the common case is one of them missing, and seeing what happens to that
 * line is the whole reason to look.
 */
const SAMPLE: Record<SmsKind, Record<string, string | null>> = {
  power_token: {
    '{firstName}': 'Kwame',
    '{landlordName}': 'Kwame Otieno',
    '{company}': 'GoSpeedNet',
    '{meterLabel}': 'Keumbu',
    '{meterNumber}': '37844 645500',
    '{token}': '1234 5678 9012 3456 7890',
    '{units}': null,
    '{amount}': 'KES 1,500',
  },
  pay_prompt: {
    '{company}': 'GoSpeedNet',
    '{paybill}': '888880',
    '{account}': '37844645500',
    '{amount}': 'KES 1,500',
    '{meterLabel}': 'Keumbu',
    '{landlordName}': 'Kwame Otieno',
  },
  job_ack_reminder: {
    '{company}': 'GoSpeedNet',
    '{reference}': 'JOB-1162',
    '{customerName}': 'Achieng Odhiambo',
    '{link}': 'https://dispatch.gospeednet.co.ke/t/abc123',
  },
};

export function SmsTemplates() {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['settings'], queryFn: api.settings });
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const stored = settings.data?.power?.smsTemplates ?? {};

  const save = useMutation({
    mutationFn: (patch: Partial<SettingsPayload['power']>) => api.updateSettings({ power: patch }),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!settings.data) return null;

  return (
    <div className="space-y-6" data-sms-templates>
      <p className="max-w-2xl text-sm text-muted-foreground">
        Leave one empty to use the wording built into the system. Anything you write here replaces
        it &mdash; but the facts in{' '}
        <span className="font-mono text-xs">{'{curly brackets}'}</span> cannot be dropped, because a
        message without them cannot do its job.
      </p>

      {error && (
        <p className="max-w-2xl rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
          {error}
        </p>
      )}

      {SMS_KINDS.map((kind) => {
        const current = drafts[kind.id] ?? stored[kind.id] ?? '';
        const dirty = drafts[kind.id] !== undefined && drafts[kind.id] !== (stored[kind.id] ?? '');
        const problem = current.trim() ? refuseSmsTemplate(kind.id, current) : null;

        /*
         * The preview is of what will actually be sent, which is the filled
         * template when there is one and nothing useful when there is not —
         * the built-in wording is assembled in the API from values this screen
         * does not have, and inventing an approximation of it here would be a
         * preview that lies on the default path.
         */
        const preview =
          current.trim() && !problem ? fillSmsTemplate(current, SAMPLE[kind.id]) : null;
        const cost = preview ? segmentsFor(preview) : null;

        return (
          <div key={kind.id} className="rounded-2xl border border-border p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="font-medium">{kind.label}</h3>
              {!stored[kind.id] && (
                <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  Built-in wording
                </span>
              )}
            </div>

            <p className="mt-1 text-sm text-muted-foreground">
              Goes to <strong className="text-foreground">{kind.who}</strong> &middot; {kind.when}
            </p>

            <div className="mt-3 grid gap-3 lg:grid-cols-2">
              <div>
                <textarea
                  className="input min-h-[8rem] w-full font-mono text-xs"
                  value={current}
                  placeholder="Empty — the built-in wording is used"
                  aria-label={`Wording for ${kind.label}`}
                  onChange={(event) =>
                    setDrafts((was) => ({ ...was, [kind.id]: event.target.value }))
                  }
                />

                <p className="mt-2 text-xs text-muted-foreground">
                  You can use:{' '}
                  {SMS_PLACEHOLDERS[kind.id].map((one) => (
                    <button
                      key={one}
                      type="button"
                      className="mr-1 rounded border border-border bg-muted px-1 font-mono hover:bg-card"
                      title={`Add ${one} to the wording`}
                      onClick={() =>
                        setDrafts((was) => ({ ...was, [kind.id]: `${current}${one}` }))
                      }
                    >
                      {one}
                    </button>
                  ))}
                </p>
              </div>

              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  What the recipient sees
                </p>

                {problem ? (
                  <p className="mt-1 rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
                    {problem}
                  </p>
                ) : preview ? (
                  <>
                    <pre className="mt-1 whitespace-pre-wrap rounded-xl border border-border bg-muted p-3 text-sm">
                      {preview}
                    </pre>
                    {cost && (
                      <p
                        className={`mt-1 text-xs ${
                          cost.segments > 1 ? 'text-accent-800' : 'text-muted-foreground'
                        }`}
                      >
                        {cost.units} characters &middot;{' '}
                        <strong>
                          {cost.segments} {cost.segments === 1 ? 'text' : 'texts'}
                        </strong>
                        {cost.unicode && (
                          <>
                            {' '}
                            &middot; contains a character outside the plain set, which drops the
                            limit from 160 to 70. A curly apostrophe does this.
                          </>
                        )}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="mt-1 rounded-xl border border-border bg-muted p-3 text-sm text-muted-foreground">
                    The built-in wording is used. Write something above to replace it.
                  </p>
                )}
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="btn-primary px-3 py-1 text-sm"
                disabled={save.isPending || !dirty || Boolean(problem)}
                title={
                  problem
                    ? 'Fix the problem above first.'
                    : !dirty
                      ? 'Nothing to save — the wording has not changed.'
                      : 'Use this wording from the next message onwards'
                }
                onClick={() => {
                  setSaved(kind.id);
                  save.mutate({
                    smsTemplates: { ...stored, [kind.id]: current.trim() },
                  } as Partial<SettingsPayload['power']>);
                }}
              >
                {save.isPending && saved === kind.id && (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                Save
              </button>

              {stored[kind.id] && (
                <button
                  type="button"
                  className="btn-quiet px-3 py-1 text-sm"
                  disabled={save.isPending}
                  title="Go back to the wording built into the system"
                  onClick={() => {
                    setDrafts((was) => ({ ...was, [kind.id]: '' }));
                    setSaved(kind.id);
                    save.mutate({
                      smsTemplates: { ...stored, [kind.id]: '' },
                    } as Partial<SettingsPayload['power']>);
                  }}
                >
                  Use the built-in wording
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
